<template>
    <vx-modal :visible="props.visible"
        @update:visible="emit('update:visible', $event)"
        :stacked="stacked"
        :preventClosing="preventClosing"
        :background="false"
        :padding="false"
        size="md">
        <template v-slot:header>
            <div class="px-6 pt-6">
                New Snippet
            </div>
        </template>
        <template v-slot:subheader>
            <div class="px-6">
                This will create a new message snippet.
            </div>
        </template>

        <form id="create-snippet"
            @submit.prevent="submit"
            class="p-6">

            <vx-label>Title</vx-label>
            <vx-input v-model="data.title"
                :error="displayFirstError(validate.title)"
                size="lg">
            </vx-input>

            <vx-label class="mt-4">Description</vx-label>
            <vx-input v-model="data.description"
                data-test="test"
                size="lg"
                :rows="4"
                :error="displayFirstError(validate.description)">
            </vx-input>

            <vx-label class="mt-4">Message</vx-label>
            <message-builder ref="messageBuilderRef"
                v-model="data.body"
                v-model:mediaUrl="data.media_url"
                v-model:mediaContentType="data.media_content_type"
                :error="displayFirstError(validate.body)"
                preview
                :disableMedia="props.disableMedia"
                :blockImages="props.blockImages"
                :blockFiles="props.blockFiles"
                :disableSnippets="true"
            ></message-builder>
        </form>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button @click.prevent="emit('update:visible', false)"
                    color="muted"
                    size="lg"
                    type="button">
                    Cancel
                    <font-awesome-icon widthAuto :icon="faCircleX"></font-awesome-icon>
                </vx-button>
                <vx-button form="create-snippet"
                    type="submit"
                    color="primary"
                    size="lg"
                    class="grow"
                    :loading="submitting">
                    Create
                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { VxButton, VxInput, VxLabel, VxModal } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { snippetsClient , actionsClient } from '~/services';
import MessageBuilder from '../MessageBuilder.vue';
import debounce from '~/utils/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleX, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { displayFirstError, validateRules } from '~/utils/validation';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';

const { $externalResults, catchError } = useFormRequestHandler();

const props = defineProps({
    visible: Boolean,
    disableMedia: Boolean,
    blockImages: {
        type: Boolean,
        default: false,
    },
    blockFiles: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:visible', 'created']);

const submitting = ref(false);
const teamId = Spark.state.currentTeam.id;
const { currentOrgUnit, getCurrentGroupId } = useCurrentGroup();

const messageBuilderRef = ref();
const stacked = computed(() => {
    if (!messageBuilderRef.value) {
        return false;
    }
    return messageBuilderRef.value.modalOpen;
});

const preventClosing = ref(false);

watch([
        () => messageBuilderRef.value?.emojisPicker,
        () => messageBuilderRef.value?.actionsDropdown
    ],
    debounce(() => {
        preventClosing.value = messageBuilderRef.value?.emojisPicker || messageBuilderRef.value?.actionsDropdown;
    }, 150)
)

const data = ref({
    title: '',
    description: '',
    media_url: '',
    media_content_type: '',
    body: '',
});

const validate = useVuelidate({
    title: {
        required,
        maxLength: maxLength(120),
    },
    description: {
        maxLength: maxLength(2000),
    },
    body: {
        required,
        maxLength: maxLength(1600),
    }
}, data, { $stopPropagation: true, $externalResults })

const clearData = () => {
    data.value = {
        title: '',
        description: '',
        body: '',
    }
}

const submit = async () => {
    const isValid = await validateRules(validate);

    if (submitting.value || !isValid) {
        return;
    }

    try {
        submitting.value = true;
        const response = await snippetsClient.createSnippet({
            teamId,
            groupId: getCurrentGroupId(),
            orgUnitId: currentOrgUnit.value?.id,
        }, data.value);

        actionsClient.send('snippet_created');
        emit('created', response.data);
        emit('update:visible', false);
        clearData();
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
}
</script>
